import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, formatTL, formatDate, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Wallet2, FileText, Scale, Truck, FolderOpen, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden mb-6">
    <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#0020C2]" />
      <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-600 font-bold">{title}</div>
    </div>
    {children}
  </div>
);

export default function PortalPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portal/overview");
        setData(data);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const summary = data?.statement?.summary || {};
  const events = data?.statement?.events || [];
  const token = localStorage.getItem("token") || "";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Üst bar */}
      <div className="bg-white border-b border-[#E5E5E5] sticky top-0 z-10">
        <div className="max-w-[1100px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/keskin-logo.png" alt="Keskin" className="w-9 h-9 object-contain" />
            <div>
              <div className="font-heading font-extrabold text-sm tracking-tight leading-none">KESKİNTUR PORTAL</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mt-0.5">Tedarikçi Portalı</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm font-semibold" data-testid="portal-supplier-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Tedarikçi</div>
            </div>
            <button
              data-testid="portal-logout-btn"
              onClick={async () => { await logout(); nav("/login"); }}
              className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#0A0A0A] hover:text-white transition-all"
            >
              <LogOut className="w-3.5 h-3.5" /> Çıkış
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 py-8" data-testid="portal-content">
        {loading ? (
          <div className="p-16 text-center text-neutral-500 flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...</div>
        ) : !data ? (
          <div className="p-16 text-center text-neutral-500 text-sm">Veriler yüklenemedi. Lütfen yöneticinizle iletişime geçin.</div>
        ) : (
          <>
            {/* Özet kartlar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {[
                ["Toplam Fatura", formatTL(summary.invoice_total || 0), "text-[#0A0A0A]", "portal-kpi-invoice"],
                ["Toplam Ödeme", formatTL(summary.payment_total || 0), "text-[#16A34A]", "portal-kpi-payment"],
                ["Bakiye", formatTL(summary.balance || 0), (summary.balance || 0) > 0 ? "text-[#DC2626]" : "text-[#16A34A]", "portal-kpi-balance"],
                ["Hakediş Toplamı", formatTL(data.hakedis_total || 0), "text-[#0020C2]", "portal-kpi-hakedis"],
              ].map(([l, v, c, tid]) => (
                <div key={l} className="bg-white border border-[#E5E5E5] rounded-md p-4" data-testid={tid}>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">{l}</div>
                  <div className={`num font-bold text-xl mt-1 ${c}`}>{v}</div>
                </div>
              ))}
            </div>

            {/* Cari Ekstre */}
            <Section icon={Wallet2} title={`Cari Ekstre (${events.length} hareket)`}>
              {events.length === 0 ? (
                <div className="p-10 text-center text-xs text-neutral-400">Henüz cari hareket bulunmuyor.</div>
              ) : (
                <div className="overflow-x-auto max-h-[420px]">
                  <table className="w-full text-xs">
                    <thead className="bg-[#FAFAFA] sticky top-0">
                      <tr>{["Tarih", "İşlem", "Açıklama", "Borç", "Alacak", "Bakiye"].map((h, i) => (
                        <th key={h} className={`px-3 py-2.5 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 3 ? "text-right" : "text-left"}`}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {events.map((e, i) => (
                        <tr key={i} className="border-t border-[#F5F5F5]">
                          <td className="px-3 py-2.5 num">{formatDate(e.date)}</td>
                          <td className="px-3 py-2.5 font-semibold">{e.label}</td>
                          <td className="px-3 py-2.5 text-neutral-500 max-w-[260px] truncate">{e.description}</td>
                          <td className="px-3 py-2.5 text-right num">{e.debit ? formatTL(e.debit) : "-"}</td>
                          <td className="px-3 py-2.5 text-right num text-[#16A34A]">{e.credit ? formatTL(e.credit) : "-"}</td>
                          <td className={`px-3 py-2.5 text-right num font-bold ${e.running_balance > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(e.running_balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            {/* Hakedişler */}
            <Section icon={Scale} title={`Hakedişler (${(data.hakedis || []).length})`}>
              {(data.hakedis || []).length === 0 ? (
                <div className="p-10 text-center text-xs text-neutral-400">Henüz hakediş kaydı bulunmuyor.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFAFA]">
                    <tr>{["Dönem", "Hakediş No", "Açıklama", "Tutar"].map((h, i) => (
                      <th key={h} className={`px-3 py-2.5 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 3 ? "text-right" : "text-left"}`}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {data.hakedis.map((h) => (
                      <tr key={h.id} className="border-t border-[#F5F5F5]">
                        <td className="px-3 py-2.5 num font-semibold">{h.month}</td>
                        <td className="px-3 py-2.5 num">{h.hakedis_no || "-"}</td>
                        <td className="px-3 py-2.5 text-neutral-500">{h.description || "-"}</td>
                        <td className="px-3 py-2.5 text-right num font-bold">{formatTL(h.amount || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            {/* Araç Evrakları */}
            <Section icon={Truck} title="Araç Evrakları">
              {(data.vehicles || []).length === 0 ? (
                <div className="p-10 text-center text-xs text-neutral-400">
                  Cari kartınızda kayıtlı plakayla eşleşen araç bulunamadı. Plaka bilgisi için yöneticinizle iletişime geçin.
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  {data.vehicles.map((v) => (
                    <div key={v.vehicle_id} data-testid={`portal-vehicle-${v.vehicle_id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="num font-bold text-sm">{v.plate}</span>
                        <span className="text-xs text-neutral-500">{v.model}</span>
                        <span className="text-[10px] uppercase tracking-widest text-neutral-400">{(v.files || []).length} evrak</span>
                      </div>
                      {(v.files || []).length === 0 ? (
                        <div className="text-xs text-neutral-400 px-3 py-2 border border-dashed border-[#E5E5E5]">Bu araç için yüklenmiş evrak yok.</div>
                      ) : (
                        <div className="space-y-1.5">
                          {v.files.map((f) => (
                            <a
                              key={f.file_id}
                              href={`${API}/vehicles/${v.vehicle_id}/files/${f.file_id}?auth=${token}`}
                              target="_blank" rel="noreferrer"
                              className="flex items-center justify-between bg-[#FAFAFA] px-3 py-2.5 text-xs border border-[#F5F5F5] hover:border-[#0020C2] transition-colors"
                              data-testid={`portal-file-${f.file_id}`}
                            >
                              <span className="flex items-center gap-2 min-w-0">
                                <FolderOpen className="w-4 h-4 text-neutral-500 shrink-0" />
                                <span className="truncate font-semibold">{f.original_filename}</span>
                              </span>
                              <ExternalLink className="w-3.5 h-3.5 text-[#0020C2] shrink-0" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <div className="text-center text-[11px] text-neutral-400 flex items-center justify-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Sorularınız için Keskintur muhasebe birimiyle iletişime geçebilirsiniz.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
